import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBaristaSettings } from '../context/BaristaSettingsContext';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { notificationTones } from '../utils/notificationTones';

const BaristaSettingsPanel = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { settings, updateSettings, resetSettings } = useBaristaSettings();
  const { testNotificationSound } = useNotificationSound();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#1f2937] rounded-lg p-6 w-full max-w-md mx-4 border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">{t('barista.settingsPanel.title')}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-3">{t('barista.settingsPanel.appearance')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  {t('barista.settingsPanel.themeColor')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'dark', label: t('barista.settingsPanel.dark'), bg: 'bg-gray-900', border: 'border-gray-700' },
                    { value: 'light', label: t('barista.settingsPanel.light'), bg: 'bg-gray-100', border: 'border-gray-300' },
                    { value: 'coffee', label: t('barista.settingsPanel.coffee'), bg: 'bg-amber-800', border: 'border-amber-600' }
                  ].map((theme) => (
                    <button
                      key={theme.value}
                      onClick={() => updateSettings({ theme: theme.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        settings.theme === theme.value
                          ? `${theme.bg} ${theme.border} border-2`
                          : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full ${theme.bg} mx-auto mb-1`}></div>
                      <span className={`text-xs ${settings.theme === theme.value ? 'text-white' : 'text-gray-300'}`}>
                        {theme.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  {t('barista.settingsPanel.fontSize')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'small', label: t('barista.settingsPanel.small') },
                    { value: 'medium', label: t('barista.settingsPanel.medium') },
                    { value: 'large', label: t('barista.settingsPanel.large') }
                  ].map((size) => (
                    <button
                      key={size.value}
                      onClick={() => updateSettings({ fontSize: size.value })}
                      className={`p-2 rounded-lg transition-colors ${
                        settings.fontSize === size.value
                          ? 'bg-[#a15507] text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <span className={size.value === 'small' ? 'text-sm' : size.value === 'large' ? 'text-lg' : 'text-base'}>
                        {size.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

                           <div>
                   <h3 className="text-lg font-semibold text-white mb-3">{t('barista.settingsPanel.soundAndNotifications')}</h3>

                   <div className="space-y-4">
                     <div className="flex items-center justify-between">
                       <label className="text-gray-300 text-sm font-medium">
                         {t('barista.settingsPanel.soundOnNewOrder')}
                       </label>
                       <button
                         onClick={() => updateSettings({ soundEnabled: !settings.soundEnabled })}
                         className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                           settings.soundEnabled ? 'bg-[#a15507]' : 'bg-gray-600'
                         }`}
                       >
                         <span
                           className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                             settings.soundEnabled ? 'translate-x-6' : 'translate-x-1'
                           }`}
                         />
                       </button>
                     </div>

                     {settings.soundEnabled && (
                       <>
                         <div>
                           <label className="block text-gray-300 text-sm font-medium mb-2">
                             {t('barista.settingsPanel.notificationTone')}
                           </label>
                           <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
                             {Object.entries(notificationTones).map(([key, tone]) => (
                               <button
                                 key={key}
                                 onClick={() => updateSettings({ notificationTone: key })}
                                 className={`p-3 rounded-lg border-2 transition-all text-left ${
                                   settings.notificationTone === key
                                     ? 'bg-[#a15507] border-[#a15507] text-white'
                                     : 'bg-gray-700 border-gray-600 hover:bg-gray-600 text-gray-300'
                                 }`}
                               >
                                 <div className="text-xs font-medium">{tone.name}</div>
                                 <div className="text-xs opacity-75 mt-1">{tone.description}</div>
                               </button>
                             ))}
                           </div>
                         </div>

                         <div>
                           <label className="block text-gray-300 text-sm font-medium mb-2">
                             {t('barista.settingsPanel.volume')}
                           </label>
                           <input
                             type="range"
                             min="0"
                             max="100"
                             value={settings.volume}
                             onChange={(e) => updateSettings({ volume: Number(e.target.value) })}
                             className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                           />
                           <div className="flex justify-between text-xs text-gray-400 mt-1">
                             <span>0%</span>
                             <span>{settings.volume}%</span>
                             <span>100%</span>
                           </div>
                         </div>

                                                   <div className="space-y-3">
                            <div className="flex gap-2">
                              <button
                                onClick={testNotificationSound}
                                className="flex-1 px-4 py-2 bg-[#a15507] hover:bg-[#924e06] text-white rounded-lg transition-colors font-medium"
                              >
                                {t('barista.settingsPanel.testSound')}
                              </button>
                              <button
                                onClick={() => updateSettings({ volume: 50 })}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors font-medium"
                              >
                                {t('barista.settingsPanel.resetVolume')}
                              </button>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <label className="text-gray-300 text-sm font-medium">
                                {t('barista.settingsPanel.repeatNotifications')}
                              </label>
                              <button
                                onClick={() => updateSettings({ repeatNotifications: !settings.repeatNotifications })}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  settings.repeatNotifications ? 'bg-[#a15507]' : 'bg-gray-600'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    settings.repeatNotifications ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                            
                            {settings.repeatNotifications && (
                              <div>
                                <label className="block text-gray-300 text-sm font-medium mb-2">
                                  {t('barista.settingsPanel.repeatInterval')}
                                </label>
                                <input
                                  type="range"
                                  min="1"
                                  max="10"
                                  value={settings.repeatInterval || 3}
                                  onChange={(e) => updateSettings({ repeatInterval: Number(e.target.value) })}
                                  className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                                />
                                <div className="flex justify-between text-xs text-gray-400 mt-1">
                                  <span>1s</span>
                                  <span>{settings.repeatInterval || 3}s</span>
                                  <span>10s</span>
                                  <span>3 times max</span>
                                </div>
                              </div>
                            )}
                          </div>
                       </>
                     )}
                   </div>
                 </div>

          <div className="flex gap-3 pt-4 border-t border-gray-700">
            <button
              onClick={resetSettings}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
            >
              {t('barista.settingsPanel.resetToDefault')}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-[#a15507] hover:bg-[#924e06] text-white rounded-lg transition-colors"
            >
              {t('barista.settingsPanel.saveAndClose')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaristaSettingsPanel;
